<template>
  <div>
    <div
      class="table-wrapper w-full overflow-x-auto"
      v-if="isLoading"
      test-id="isLoading"
    >
      <table-loader />
    </div>
    <div
      class="table-wrapper w-full overflow-x-auto"
      v-else-if="!noTransactions"
    >
      <el-table
        test-id="table"
        class="w-full relative cursor-pointer font-normal text-root-2 text-xs lg:text-sm overflow-x-scroll"
        style="min-width: 100%"
        :data="tableData"
        max-height="600"
        @row-click="clickedRow"
      >
        <el-table-column :width="breakpoints.smAndDown ? '200' : 'auto'">
          <template slot="header">
            <div
              class="flex items-center justify-between px-2 lg:px-5 font-semibold"
            >
              <p class="text-root-2 text-xs lg:text-sm whitespace-nowrap">
                Transaction Types
              </p>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="flex items-center px-2 lg:px-5 gap-x-3">
              <arrow-down :transactionType="transactionType" />
              <p
                style="word-break: normal"
                class="text-root-4 font-normal capitalize text-xs lg:text-sm flex gap-x-4"
              >
                <span>
                  {{
                    formatServerName(
                      `${scope.row.type} (${scope.row.method})`
                    ) || '----'
                  }}
                </span>
                <el-tooltip
                  :disabled="actor(scope) !== 'admin'"
                  content="Transaction appended by an admin"
                  placement="top"
                >
                  <div v-if="actor(scope) === 'admin'">
                    <admin-transaction />
                  </div>
                </el-tooltip>
              </p>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="200">
          <template slot="header">
            <div class="flex items-center px-2 lg:px-5 font-semibold">
              <p
                style="word-break: normal"
                class="text-root-2 text-xs lg:text-sm"
              >
                Amount
              </p>
              <el-tooltip
                v-if="!isDashboardUse"
                content="Sort"
                placement="top"
                effect="light"
              >
                <div @click="handleSort('amount')" class="ml-2">
                  <sort-icon :sort-direction="sortDirection.amount" />
                </div>
              </el-tooltip>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="">
              <p
                class="text-root-2 text-xs lg:text-sm font-semibold px-2 lg:px-5 overflow-x-scroll"
                style="word-break: normal"
              >
                {{
                  scope.row.type == $transactionType.DEPOSIT
                    ? parseMoneyAll(
                        scope.row.depositedAmount,
                        scope.row.depositedCurrency
                      )
                    : parseMoneyAll(scope.row.amount, scope.row.currency)
                }}
              </p>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="240">
          <template slot="header">
            <div class="font-semibold flex items-center lg:pl-5">
              <p
                style="word-break: normal"
                class="text-root-2 text-center text-xs lg:text-sm"
              >
                Status
              </p>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="text-xs flex items-start font-normal ml-4 capitalize">
              <el-tooltip
                v-if="scope.row.status === $STATUS.AWAITING_BUSINESS_APPROVAL"
                content="Awaiting Business Approval"
                placement="top"
                effect="light"
              >
                <div
                  :class="statusClass(scope.row.status)"
                  class="py-1.5 px-3 rounded-lg"
                >
                  {{ formatServerName(scope.row.status) }}
                </div>
              </el-tooltip>

              <div
                v-else
                :class="statusClass(scope.row.status)"
                class="py-1.5 px-3 rounded-lg flex items-center gap-1"
              >
                {{ formatServerName(scope.row.status) }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="240">
          <template slot="header">
            <div class="flex items-center lg:px-5 font-semibold">
              <p
                style="word-break: normal"
                class="text-root-2 text-xs lg:text-sm"
              >
                Date
              </p>
              <el-tooltip
                v-if="!isDashboardUse"
                content="Sort"
                placement="top"
                effect="light"
              >
                <div @click="handleSort('time')" class="ml-2">
                  <sort-icon :sort-direction="sortDirection.time" />
                </div>
              </el-tooltip>
            </div>
          </template>
          <template slot-scope="scope">
            <div
              class="flex items-center justify-between px-2 lg:px-5 font-normal text-xs lg:text-sm"
            >
              <el-tooltip
                :content="tooltipDateContent(scope.row.createdAt)"
                placement="top"
                effect="light"
              >
                <span>{{ parseRelativeTime(scope.row.createdAt) }}</span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="50">
          <template slot="header"> </template>
          <template slot-scope="">
            <arrow-right-icon />
          </template>
        </el-table-column>
      </el-table>
      <div
        class="flex flex-col md:flex-row gap-2 justify-between md:items-center pt-4"
        v-if="!isDashboardUse"
      >
        <pagination-details
          :page-size="page.size"
          :current-page="page.page"
          :total-count="page.totalCount"
        />
        <el-pagination
          @current-change="handleCurrentChange"
          @prev-click="previous"
          @next-click="next"
          :page-size="page.size"
          :current-page="page.page"
          :total="page.totalCount"
          layout="prev, pager, next"
          hide-on-single-page
          background
        >
        </el-pagination>
      </div>
    </div>

    <transaction-empty-state use="receive-payment" v-else>
      <h3 class="font-semibold text-root-2 capitalize" test-id="emptyState">
        No {{ transactionType }} Transaction
      </h3>
      <p
        class="font-light text-root-4 text-xs flex items-center justify-center mt-2"
      >
        <span class="w-3/4 text-center">
          Your {{ transactionType }} transactions show up here.
        </span>
      </p>
    </transaction-empty-state>

    <!-- Dialogs -->
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authAction"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeAuthDialog"
      @approveCancelWithrawalRequest="approveCancelWithrawalRequest"
    />

    <reason-dialog
      v-if="showCancelModal"
      :showDialog="showCancelModal"
      @closeDialog="closeCancelDialog"
      @handleCancelreason="handleCancelreason"
    />

    <!-- transaction drawer -->
    <transaction-drawer
      v-if="showTransactionDrawer"
      :showDrawer="showTransactionDrawer"
      :transactionDetails="transactionDetails"
      :rateIncreased="transactionHasRateChange"
      :transactionType="transactionType"
      @closeDrawer="closeDrawer"
      @handleApproveWithdrawal="handleApproveWithdrawal"
      @handleCancelWithdrawal="handleCancelWithdrawal"
    />
  </div>
</template>

<script>
import {
  ParseMoneyAll,
  ParseLocalTime,
  DaysAgo,
  StatusClass,
  FormatServerName,
  ParseRelativeTime
} from '@/utils/helpers.js'
import breakpoints from '@/utils/breakpoints.js'
import notification from '@/mixins/notification.js'
import transactions from '@/mixins/transactions.js'
import { sortHandlerMixin } from '@/mixins/useSort.js'
import fetchAllTours from '@/mixins/appTourApi.js'
import tooltipDateContent from '@/mixins/app.js'

export default {
  mixins: [
    transactions,
    tooltipDateContent,
    sortHandlerMixin,
    fetchAllTours,
    notification
  ],
  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: () => 0
    },
    isLoading: {
      type: Boolean,
      default: () => false
    },
    transactionType: {
      type: String,
      default: () => ''
    },
    page: {
      type: Object,
      default: () => {}
    },
    use: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      payload: {
        page: 1,
        size: 10,
        status: '',
        type: '',
        from: '',
        to: ''
      },
      breakpoints,
      showTransactionDrawer: false,
      showCancelModal: false,
      transactionID: '',
      authAction: '',
      cancelReason: '',
      SelectedTransactionType: '',
      transactionHasRateChange: false,
      transactionDetails: {},
      skeletonRows: [1, 2, 3, 4],
      loading: false,
      showVerifyAuthDialog: false
    }
  },
  components: {
    AdminTransaction: () => import('@/assets/icons/AdminTransaction.vue'),
    TransactionEmptyState: () =>
      import('@/components/empty-states/TransactionEmptyState.vue'),
    ArrowDown: () => import('@/assets/v2/icons/ArrowDown.vue'),
    ArrowRightIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    SortIcon: () => import('@/assets/v2/icons/SortIcon.vue'),
    TransactionDrawer: () =>
      import(
        '@/components/v2/transactions/drawers/transactionDrawer/Index.vue'
      ),
    PaginationDetails: () =>
      import('@/components/v2/app/PaginationDetails.vue'),
    TableLoader: () =>
      import('@/components/v2/skeletalLoaders/TableLoader.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog.vue'),
    ReasonDialog: () => import('@/components/v2/common/ReasonDialog.vue')
  },
  computed: {
    entryIndex () {
      return this.page.size * (this.page.page - 1) + 1
    },
    endIndex () {
      if (this.page.totalCount > this.page.size) {
        const end = this.page.page * this.page.size
        return end > this.page.totalCount ? this.page.totalCount : end
      } else {
        return this.page.totalCount
      }
    },
    noTransactions () {
      return !this.tableData.length && !this.isLoading
    },
    isDashboardUse () {
      return this.use === 'dashboard'
    }
  },
  methods: {
    handleOpenFromNotification () {
      if (this.isNotificationRoute) {
        this.showTransactionDrawer = true
      }
    },
    closeAuthDialog () {
      this.showVerifyAuthDialog = false
    },
    closeCancelDialog () {
      this.showCancelModal = false
    },
    handleCancelreason (reason) {
      this.cancelReason = reason
      this.showCancelModal = false
      this.showVerifyAuthDialog = true
    },
    handleApproveWithdrawal (actionType) {
      this.authAction = actionType
      this.showTransactionDrawer = false
      this.showVerifyAuthDialog = true
      if (this.isNotificationRoute) {
        this.resetNotificationDetails()
        this.resetDrawerOpenedByNotification()
        this.getAllNotifications()
      }
    },
    handleCancelWithdrawal (actionType) {
      this.authAction = actionType
      this.showTransactionDrawer = false
      this.showCancelModal = true
      if (this.isNotificationRoute) {
        this.resetNotificationDetails()
        this.resetDrawerOpenedByNotification()
        this.getAllNotifications()
      }
    },
    actor (scope) {
      return scope.row.actor
    },
    parseRelativeTime (timeToPick) {
      return ParseRelativeTime(timeToPick)
    },
    statusClass (status) {
      return StatusClass(status)
    },
    formatServerName (status) {
      let formattedName
      switch (status) {
        case this.$STATUS.AWAITING_BUSINESS_APPROVAL:
          formattedName = this.$STATUS.AWAITING_APPROVAL
          break
        case this.$STATUS.DISAPPROVED:
          formattedName = this.$STATUS.CANCELLED
          break
        default:
          formattedName = status
      }

      return FormatServerName(formattedName)
    },
    daysAgo (date) {
      return DaysAgo(date)
    },
    parseLocalTime (time) {
      ParseLocalTime(time)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    next () {
      this.payload.page++
    },
    previous () {
      this.payload.page--
    },
    handleCurrentChange (val) {
      this.resetSortDirection()
      this.$emit('handleCurrentChange', {
        val
      })
    },
    closeDrawer () {
      this.showTransactionDrawer = false
      this.transactionDetails = {}
      if (this.isNotificationRoute) {
        this.resetNotificationDetails()
        this.resetDrawerOpenedByNotification()
        this.getAllNotifications()
      }
    },
    clickedRow (row) {
      this.transactionDetails = row
      this.transactionHasRateChange = this.rateIncreased(row)
      this.showTransactionDrawer = true
    },
    displayTimeDate (row) {
      const timeToPick =
        row.status === this.$STATUS.COMPLETED ? row.completedAt : row.createdAt
      return this.tooltipDateContent(timeToPick)
    },
    approveCancelWithrawalRequest (twoFaCode) {
      const payload = {
        referenceID: this.transactionDetails.id,
        transactionType: 'withdrawal',
        twoFaCode: twoFaCode.code,
        action:
          this.authAction === 'approveWithdrawal' ? 'approve' : 'disapprove',
        cancelReason:
          this.authAction === 'cancelWithdrawal' ? this.cancelReason : ''
      }
      this.$store
        .dispatch('withdrawal/approveCancelWithdrawal', payload)
        .then(() => {
          this.showVerifyAuthDialog = false
          this.$notify({
            title:
              this.authAction === 'approveWithdrawal'
                ? 'Approved'
                : 'Cancelled',
            message:
              this.authAction === 'approveWithdrawal'
                ? 'Withdrawal approved successfully.'
                : 'Withdrawal cancelled successfully.',
            type: 'success'
          })
          this.getTransactions()
        })
        .catch((error) => {
          this.showVerifyAuthDialog = false
          const err = error.response
            ? error?.response?.data?.error?.publicMessage
            : error
          this.$notify({
            title: this.httpErrorMessage.customMessage,
            message: err || '',
            type: 'error'
          })
        })
    }
  },
  watch: {
    '$route.params.type': {
      handler: function (newValue) {
        if (newValue) {
          this.handleOpenFromNotification()
        }
      },
      immediate: true
    }
  },
  mounted () {
    this.handleOpenFromNotification()
  }
}
</script>

<style scoped>
>>> .el-tooltip__popper.is-dark,
>>> .el-tooltip__popper.is-light,
>>> .el-tooltip__popper {
  background: white !important;
}

>>> .el-table {
  padding: 0;
  background: transparent;
  border-collapse: separate;
}

>>> .el-table table {
  border-spacing: 0 16px;
}
.scrollable-container {
  max-height: 60vh;
  overflow-y: auto;
}

>>> .el-table th {
  border: none !important;
  background: #f7f7f7;
  padding: 0;
}

>>> .el-table tr {
  background: white;
  border-radius: 8px;
}
>>> .el-table tr:hover {
  cursor: pointer;
}
>>> .el-table td {
  border-bottom: none !important;
  background: white;
}

>>> .el-table td:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
>>> .el-table td:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

>>> .el-table::before {
  height: 0px;
}
>>> .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background: white !important;
}
>>> .el-pagination.is-background .btn-next.disabled,
.el-pagination.is-background .btn-next:disabled,
.el-pagination.is-background .btn-prev.disabled,
.el-pagination.is-background .btn-prev:disabled,
.el-pagination.is-background .el-pager li.disabled {
  background: green !important;
}
>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}

>>> .el-pagination.is-background .btn-prev,
.el-pagination.is-background .btn-next,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}

>>> li.number.active {
  background-color: var(--color-primary) !important;
  border: none !important;
  color: white !important;
  border-radius: 7.53065px !important;
}

>>> li.number {
  border: none !important;
  font-weight: 500;
  font-size: 12px;
  color: var(--text-2);
  background: transparent !important;
  /* color: var(--color-primary) !important ; */
}

>>> li.number:hover {
  border: 0.941331px solid var(--color-primary) !important;
  border-radius: 7.53065px !important;
  color: var(--text-2) !important;
}

>>> .el-input--prefix .el-input__inner {
  border: 1px solid var(--border-1);
  border-radius: 8px;
  background: var(--background-10);
}

>>> .el-table__empty-text {
  display: none;
  height: 0;
}

>>> .el-table__empty-block {
  height: 0 !important;
  min-height: 0;
}

>>> .el-tooltip__popper.is-light {
  background: white !important;
  border-color: white !important;
}
</style>
